import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useIntl, FormattedMessage } from 'react-intl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import FlexColumn from '../../shared/components/layouts/FlexColumn'
import FormikTextField from '../../shared/components/formik/FormikTextField'
import useTwoFactorAuth from '../../shared/resources/api/v1/auth/useTwoFactorAuth'

const TwoFactorDeactivateDialog = ({ open, onClose, onDeactivated }) => {
  const intl = useIntl()
  const { deactivate } = useTwoFactorAuth()

  const validationSchema = Yup.object({
    code: Yup.string()
      .matches(/^\d{6}$/, intl.formatMessage({ id: 'settings.twoFactor.error.format' }))
      .required(intl.formatMessage({ id: 'login.error.required' })),
  })

  const formik = useFormik({
    initialValues: { code: '' },
    validationSchema,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      setStatus(null)
      deactivate(values.code)
        .then(() => {
          onDeactivated()
        })
        .catch(() => {
          setStatus(intl.formatMessage({ id: 'settings.twoFactor.error.invalid' }))
        })
        .finally(() => {
          setSubmitting(false)
        })
    },
  })

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="settings.twoFactor.deactivate.title" />
      </DialogTitle>
      <DialogContent>
        <FlexColumn gap={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            <FormattedMessage id="settings.twoFactor.deactivate.message" />
          </Typography>

          {formik.status && (
            <Alert severity="error">{formik.status}</Alert>
          )}

          <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
            <FormikTextField
              formik={formik}
              name="code"
              label={<FormattedMessage id="settings.twoFactor.deactivate.codeLabel" />}
              autoFocus
              autoComplete="one-time-code"
            />
          </Box>
        </FlexColumn>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={formik.isSubmitting}>
          <FormattedMessage id="app.actions.cancel.label" />
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={formik.handleSubmit}
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <CircularProgress size={20} />
          ) : (
            <FormattedMessage id="settings.twoFactor.deactivate.submit" />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TwoFactorDeactivateDialog
